/**
 * @lines98/client/lib/popover-anchor — общий помощник «поставить поповер РЯДОМ с
 * триггером».
 *
 * ЗАЧЕМ (модульность, CLAUDE.md «ствол → ветки»). Логику «native <dialog> с
 * showModal() центрируется по экрану — перебить и прижать к кнопке-триггеру, зажав
 * в пределах вьюпорта, с раскрытием вверх если снизу не помещается» использует и
 * `info-popover`, и `picker`. Чтобы не копировать её в каждый компонент, она живёт
 * ОДНОЙ функцией здесь. Координаты — от `getBoundingClientRect()`; зазоры — от
 * единого `--m` (без хардкода).
 *
 * Поповер ставится под триггером, прижат к его левому краю; если снизу не хватает
 * места — раскрывается над триггером; по горизонтали зажимается в [m, vw−w−m].
 *
 * ВЫСОТА — по РЕАЛЬНО доступному месту (owner-decision 2026-06-07), НЕ флэт-потолок.
 * Раньше высоту резал CSS `min(50·--m, 70dvh)` в picker.css: 50·--m — угаданное число,
 * 70dvh — нативная единица-источник (§4.2 запрещает), и оба держали список короче
 * экрана → скролл при свободном месте. Теперь анкор меряет натуральную высоту контента
 * и доступное место над/под триггером (JS-замер `clientHeight` + токен `--m`), выбирает
 * сторону и ставит `max-height` = доступное место ТОЛЬКО когда контент не влезает —
 * иначе список показан целиком без скролла. Симметрично решению по ширине.
 */

/**
 * Поставить `dialog` рядом с `trigger`. Вызывать ПОСЛЕ `dialog.showModal()` (нужны
 * реальные размеры поповера для измерения). Идемпотентно — можно дёргать на resize.
 */
export function positionNearTrigger(dialog: HTMLElement, trigger: HTMLElement): void {
  const t = trigger.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const m = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--m')) || 8;
  const gap = m * 0.5;

  dialog.style.position = 'fixed';
  dialog.style.margin = '0';
  dialog.style.right = 'auto';
  dialog.style.bottom = 'auto';

  // --- Высота по доступному месту (не флэт-потолок). Снимаем прежний inline-лимит и
  // меряем НАТУРАЛЬНУЮ высоту контента (сколько поповер хочет занять без ограничения).
  dialog.style.maxHeight = '';
  const naturalHeight = dialog.getBoundingClientRect().height;

  // Реально свободное место над/под триггером (JS-замер vh/m, §4.2 — не нативный dvh).
  const spaceBelow = vh - t.bottom - gap - m; // от низа триггера до края экрана
  const spaceAbove = t.top - gap - m; // от верха триггера до верха экрана

  // Сторона раскрытия: вниз, если контент влезает; иначе вверх, если влезает; иначе —
  // сторона с бОльшим местом (минимальный скролл).
  let openAbove: boolean;
  if (naturalHeight <= spaceBelow) openAbove = false;
  else if (naturalHeight <= spaceAbove) openAbove = true;
  else openAbove = spaceAbove > spaceBelow;

  const avail = Math.max(0, openAbove ? spaceAbove : spaceBelow);
  // max-height = доступное место ТОЛЬКО если контент не влезает (иначе не трогаем —
  // список натуральной высоты, скролла нет). Скролл (overflow-y:auto) включится лишь
  // когда список не помещается даже на лучшей стороне.
  if (naturalHeight > avail) dialog.style.maxHeight = `${Math.floor(avail)}px`;
  const height = Math.min(naturalHeight, avail);

  // Горизонталь: левый край поповера = левый край триггера, зажат в [m, vw−d.w−m].
  const d = dialog.getBoundingClientRect();
  let left = t.left;
  left = Math.max(m, Math.min(left, vw - d.width - m));

  // Вертикаль по выбранной стороне; защитный зажим в пределах вьюпорта.
  let top = openAbove ? t.top - gap - height : t.bottom + gap;
  top = Math.max(m, Math.min(top, vh - height - m));

  dialog.style.left = `${Math.round(left)}px`;
  dialog.style.top = `${Math.round(top)}px`;
}
